import { noop } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppState } from '@work4all/components';
import { useCtrlMouseClickLink } from '@work4all/components/lib/hooks/use-ctrl-mouse-click-link';

import { CLONEABLE_ENTITIES, useCloneEntityMutation } from '@work4all/data';
import { usePermissions } from '@work4all/data/lib/hooks/use-permissions';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  MaskConfig,
  MaskTemplateEntity,
  OnOpenMask,
} from '../../containers/mask-overlays/mask-overlay/types';

import { buildConvertTemplateAndParams } from './utils/convert-utils';

export interface EntityHandlersProps {
  entity: Entities;
  onOpenMask: OnOpenMask;
  template?: MaskTemplateEntity | null;
  entries: { id?: string | number }[];
}

export function useEntityHandlers(props: EntityHandlersProps) {
  const {
    entity: originalEntity,
    onOpenMask,
    template = null,
    entries,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { canAdd, canEdit: canEditChecker } = usePermissions();
  const { forcedShareBaseUrl } = useAppState();
  const cloneEntity = useCloneEntityMutation();
  const { getLink } = useCtrlMouseClickLink();

  const entity = useMemo(
    () =>
      originalEntity === Entities.inboundInvoice
        ? Entities.reViewModel
        : originalEntity,
    [originalEntity]
  );

  const canCreate = canAdd({ entity, withExtraInfos: true });
  const canEdit = canEditChecker({
    entity,
    record: entries[0],
    withExtraInfos: true,
  });

  const create = useMemo(() => {
    if (!canCreate.allowed)
      return {
        handler: (_params?: MaskConfig['params']) => {
          //nop
        },
        disabled: true,
        disableReason: canCreate.reason,
      };
    const mask = onOpenMask();

    const getHref = mask.getHref
      ? () => {
          return mask.getHref({
            entity,
            template,
          });
        }
      : undefined;

    return {
      getHref,
      handler: (params?: MaskConfig['params']) =>
        mask.handler({
          entity,
          id: null,
          template,
          params,
        }),
    };
  }, [canCreate, entity, onOpenMask, template]);

  const edit = useMemo(() => {
    if (entries?.length === 1 && !canEdit.allowed) {
      return {
        handler: noop,
        disabled: true,
        disableReason: canEdit.reason,
      };
    }
    const mask = onOpenMask();
    const selectedIds = entries.map((x) => x.id);

    const getHref = mask.getHref
      ? (input: string) => {
          return mask.getHref({ entity, id: input || selectedIds[0] });
        }
      : undefined;

    return {
      getHref,
      handler: () => {
        if (selectedIds.length === 1) {
          mask.handler({ entity, id: selectedIds[0] });
        }
      },
    };
  }, [canEdit, entity, onOpenMask, entries]);

  const convert = useCallback(
    (convertTo: Entities) => {
      const { handler } = onOpenMask();

      const { template, params } = buildConvertTemplateAndParams(
        entity,
        convertTo,
        entries
      );

      handler({
        entity: convertTo,
        id: null,
        template,
        params,
      });
    },
    [entity, onOpenMask, entries]
  );

  const share = useCallback(() => {
    const mask = onOpenMask();
    const { origin, pathname } = window.location;
    if (!entries[0]) return;
    const selectedId = entries[0].id;
    const baseUrl = forcedShareBaseUrl || origin;

    let link = `${baseUrl}${pathname}`;

    if (link.includes(String(selectedId))) {
      link += '?fullscreen=true';
    } else {
      link += `/${mask.getHref({
        entity,
        id: selectedId,
      })}?fullscreen=true`;
    }

    navigator.clipboard.writeText(link);
    enqueueSnackbar(t('COMMON.LINK_COPIED_TO_CLIPBOARD'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  }, [enqueueSnackbar, entity, forcedShareBaseUrl, onOpenMask, entries, t]);

  const clone = useMemo(() => {
    if (!canCreate.allowed) {
      return {
        handler: () => {
          //nothing
        },
        disabled: true,
        disableReason: canCreate.reason,
      };
    }

    if (!entries[0] || entries.length > 1) return;
    const selectedId = entries[0].id;

    return {
      disabled: false,
      handler: () => {
        cloneEntity({
          id: Number(selectedId),
          entity,
        });
      },
    };
  }, [canCreate, entries, cloneEntity, entity]);

  const openMaskTab = useCallback(
    (openTab: string) => {
      const mask = onOpenMask();

      if (!entries[0]) return;
      const selectedId = entries[0].id;

      mask.handler({ entity, id: selectedId, openTab });
    },
    [entity, onOpenMask, entries]
  );

  const openInNewTab = useMemo(() => {
    return {
      getPath: (id: string) => {
        return getLink({ entity, id });
      },
    };
  }, [entity, getLink]);

  return {
    create: exclude(create, entity, EXCLUDE_CREATE_AND_EDIT),
    edit: exclude(edit, entity, EXCLUDE_CREATE_AND_EDIT),
    convert: include(convert, entity, CONVERT_ENTITIES),
    share,
    clone: include(clone, entity, [...CLONEABLE_ENTITIES]),
    openMaskTab,
    openInNewTab,
  };
}

const CONVERT_ENTITIES = [
  Entities.appointment,
  Entities.callMemo,
  Entities.checkList,
  Entities.document,
  Entities.eMail,
  Entities.letter,
  Entities.note,
  Entities.task,
  Entities.visitationReport,
];

const EXCLUDE_CREATE_AND_EDIT = [
  Entities.productionContract,
  Entities.visitationReport,
  Entities.mailboxContent,
];

function include<T>(obj: T, entity: Entities, entities: Entities[]) {
  return entities.includes(entity) ? obj : undefined;
}

function exclude<T>(obj: T, entity: Entities, entities: Entities[]) {
  return !entities.includes(entity) ? obj : undefined;
}
